import { useCallback, useState } from "react";
import { useRouter } from "solito/router";

import { issueUrl } from "@medbillai/utils/urls";

import { useAlerts } from "../provider/alerts";
import {
  type UploadError,
  type UploadFile,
  useDocumentUpload,
} from "../provider/documents";
import { analyticsService } from "./analytics";
import { useCreateIssue } from "./useCreateIssue";

const UPLOAD_BILL_HOME_SOURCE = "home_screen_upload_bill_cta";

export function useUploadBillFromHome() {
  const { push } = useRouter();
  const { showAlert } = useAlerts();
  const { upload } = useDocumentUpload();

  const { openIssue } = useCreateIssue({
    caseType: "BILL_ISSUE",
    title: "Help with a bill",
    existingMessages: [],
    analyticsProperties: {
      source: "WELCOME_SCREEN",
      action: "UPLOAD_BILL_CTA",
    },
  });

  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const openUploadSheet = useCallback(() => {
    analyticsService.trackEvent("CaseIntakeStarted", {
      source: UPLOAD_BILL_HOME_SOURCE,
    });
    setIsUploadSheetOpen(true);
  }, []);

  const closeUploadSheet = useCallback(() => {
    setIsUploadSheetOpen(false);
  }, []);

  const handleUploadError = useCallback(
    (err: UploadError) => {
      setIsUploading(false);
      showAlert({
        type: "error",
        message:
          err.message ??
          "Unable to upload your document, please try again later.",
      });
    },
    [showAlert],
  );

  const handleFilesLoaded = useCallback(
    async (uploadFiles: UploadFile[]) => {
      await upload({
        uploadFiles,
        key: `home-upload-${Date.now()}`,
        onUploadStarted: () => {
          setIsUploadSheetOpen(false);
          setIsUploading(true);
        },
        onUploadComplete: async (documentId: string) => {
          const newIssueId = await openIssue({ documentId });

          if (newIssueId) {
            push(issueUrl({ issueId: newIssueId, skipPageUILoad: true }));
          }

          setIsUploading(false);
        },
        onUploadError: handleUploadError,
      });
    },
    [upload, openIssue, push, handleUploadError],
  );

  return {
    isUploadSheetOpen,
    isUploading,
    openUploadSheet,
    closeUploadSheet,
    handleFilesLoaded,
    handleUploadError,
  };
}
